// Security → two-factor authentication (TOTP) self-service card for the Settings page.
//
// Flow: probe /auth/me for current status. When disabled, "Enable" calls /auth/mfa/enroll,
// then shows the scannable QR (a data-URI <img>, allowed by the CSP img-src 'self' data:)
// plus the base32 secret and a 6-digit field that /auth/mfa/confirm verifies. When enabled,
// a code field drives /auth/mfa/disable. All mutations carry the CSRF token via the client.

import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import { C, MONO } from '../lib/tokens';
import { PBTN, GBTN, H2 } from '../lib/styles';

type Mode = 'loading' | 'disabled' | 'enrolling' | 'enabled';

function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return 'That code was not valid. Enter the current 6-digit code and try again.';
    return e.message || fallback;
  }
  return fallback;
}

const CODE_FIELD: React.CSSProperties = {
  height: 34,
  width: 132,
  background: '#0A0F19',
  border: '1px solid ' + C.ln,
  borderRadius: 6,
  color: C.tx,
  fontSize: 15,
  padding: '0 12px',
  fontFamily: MONO,
  letterSpacing: '.3em',
  textAlign: 'center',
};

export function MfaCard() {
  const [mode, setMode] = useState<Mode>('loading');
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [disarm, setDisarm] = useState(false); // enabled mode: reveal the disable code field
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((u) => {
        if (alive) setMode(u.mfa_enabled ? 'enabled' : 'disabled');
      })
      .catch(() => {
        if (alive) setMode('disabled');
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (mode === 'enrolling' || disarm) codeRef.current?.focus();
  }, [mode, disarm]);

  function setDigits(v: string): void {
    setCode(v.replace(/[^0-9]/g, '').slice(0, 8));
    if (error) setError(null);
  }

  async function startEnroll(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.mfaEnroll();
      const dataUri = await QRCode.toDataURL(res.otpauth_uri, { margin: 1, width: 176 });
      setSecret(res.secret);
      setQr(dataUri);
      setCode('');
      setMode('enrolling');
    } catch (e) {
      setError(errText(e, 'Could not start enrollment. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnroll(): Promise<void> {
    if (busy || code.trim().length < 6) return;
    setBusy(true);
    setError(null);
    try {
      await api.mfaConfirm(code.trim());
      setSecret('');
      setQr('');
      setCode('');
      setMode('enabled');
      setNotice('MFA enabled');
    } catch (e) {
      setError(errText(e, 'Could not confirm enrollment.'));
    } finally {
      setBusy(false);
    }
  }

  function cancelEnroll(): void {
    setSecret('');
    setQr('');
    setCode('');
    setError(null);
    setMode('disabled');
  }

  async function disable(): Promise<void> {
    if (busy || code.trim().length < 6) return;
    setBusy(true);
    setError(null);
    try {
      await api.mfaDisable(code.trim());
      setCode('');
      setDisarm(false);
      setMode('disabled');
      setNotice('MFA disabled');
    } catch (e) {
      setError(errText(e, 'Could not disable MFA.'));
    } finally {
      setBusy(false);
    }
  }

  const statusColor = mode === 'enabled' ? C.gn : mode === 'loading' ? C.dm : C.am;
  const statusLabel =
    mode === 'loading' ? 'Checking…' : mode === 'enabled' ? 'Enabled' : mode === 'enrolling' ? 'Setup in progress' : 'Disabled';

  return (
    <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={H2}>Security · Two-factor authentication</h2>
        <span
          role="status"
          style={{
            marginLeft: 'auto',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: statusColor,
            background: 'rgba(255,255,255,.04)',
            border: '1px solid ' + statusColor + '55',
            borderRadius: 4,
            padding: '2px 8px',
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>
        Protect sign-in with a time-based one-time code from an authenticator app (Google Authenticator, 1Password, Authy).
        Codes are required in addition to your password.
      </div>

      {notice && (
        <div role="status" style={{ fontSize: 11.5, color: C.gn, background: 'rgba(63,191,127,.1)', border: '1px solid rgba(63,191,127,.35)', borderRadius: 6, padding: '7px 10px' }}>
          {notice}
        </div>
      )}
      {error && (
        <div role="alert" style={{ fontSize: 11.5, color: C.rd, background: 'rgba(228,91,91,.1)', border: '1px solid rgba(228,91,91,.35)', borderRadius: 6, padding: '7px 10px', lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      {mode === 'disabled' && (
        <div>
          <button onClick={startEnroll} disabled={busy} style={{ ...PBTN, opacity: busy ? 0.7 : 1, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Preparing…' : 'Enable two-factor authentication'}
          </button>
        </div>
      )}

      {mode === 'enrolling' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 11.5, color: C.b9, lineHeight: 1.5 }}>
            1. Scan this QR code with your authenticator app, or enter the secret key manually.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {qr && (
              <img
                src={qr}
                alt="Two-factor QR code"
                width={176}
                height={176}
                style={{ borderRadius: 6, border: '1px solid ' + C.ln, background: '#fff', padding: 6 }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
              <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Secret key</span>
              <code
                style={{
                  fontFamily: MONO,
                  fontSize: 12.5,
                  color: C.tx,
                  background: '#0A0F19',
                  border: '1px solid ' + C.ln,
                  borderRadius: 6,
                  padding: '8px 10px',
                  wordBreak: 'break-all',
                  userSelect: 'all',
                }}
              >
                {secret}
              </code>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="wt-mfa-code" style={{ fontSize: 11.5, color: C.b9 }}>
              2. Enter the current 6-digit code to finish.
            </label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                id="wt-mfa-code"
                ref={codeRef}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={8}
                placeholder="123456"
                value={code}
                onChange={(e) => setDigits(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void confirmEnroll();
                }}
                style={CODE_FIELD}
              />
              <button onClick={confirmEnroll} disabled={busy || code.trim().length < 6} style={{ ...PBTN, opacity: busy || code.trim().length < 6 ? 0.6 : 1, cursor: busy || code.trim().length < 6 ? 'default' : 'pointer' }}>
                {busy ? 'Verifying…' : 'Confirm & enable'}
              </button>
              <button onClick={cancelEnroll} disabled={busy} style={GBTN}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === 'enabled' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>
            Two-factor authentication is active. A code from your authenticator app is required at every sign-in.
          </div>
          {!disarm ? (
            <div>
              <button onClick={() => { setDisarm(true); setError(null); setNotice(null); }} style={GBTN}>
                Disable two-factor authentication
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label htmlFor="wt-mfa-disable-code" style={{ fontSize: 11.5, color: C.b9 }}>
                Enter a current 6-digit code to confirm.
              </label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  id="wt-mfa-disable-code"
                  ref={codeRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={8}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setDigits(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void disable();
                  }}
                  style={CODE_FIELD}
                />
                <button
                  onClick={disable}
                  disabled={busy || code.trim().length < 6}
                  style={{
                    height: 34,
                    background: 'none',
                    border: '1px solid ' + C.rd + '88',
                    borderRadius: 6,
                    color: C.rd,
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: '0 14px',
                    fontFamily: 'inherit',
                    opacity: busy || code.trim().length < 6 ? 0.6 : 1,
                    cursor: busy || code.trim().length < 6 ? 'default' : 'pointer',
                  }}
                >
                  {busy ? 'Disabling…' : 'Disable'}
                </button>
                <button onClick={() => { setDisarm(false); setCode(''); setError(null); }} disabled={busy} style={GBTN}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
